import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import adminAxios from "../../../api/adminAxios";
import { baseurl } from "../../config/url";
import * as XLSX from "xlsx";

const money = (n) => {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return "₹0";
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const dt = (d) => {
  if (!d) return "N/A";
  const x = new Date(d);
  return isNaN(x.getTime()) ? "N/A" : x.toLocaleString("en-IN");
};

const productName = (p) => p?.name || "N/A";
const sellerName = (s) => s?.name || s?.companyName || "N/A";

const buildImageUrlFromId = (id) => {
  if (!id) return "";
  return `${baseurl}/uploads/${id}`;
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await adminAxios.get(`/api/admin/orders/${orderId}`);
        const o = res.data?.order || res.data;

        if (!o?._id) throw new Error("Order not found");

        setOrder(o);
      } catch (e) {
        setErr(e.response?.data?.message || e.message);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) load();
  }, [orderId]);

  // =========================
  // ✅ EXCEL DOWNLOAD FUNCTION
  // =========================
  const downloadExcel = () => {
    if (!order) return;

    const items = Array.isArray(order.items) ? order.items : [];

    const data = items.map((it) => {
      const p = it.product || {};
      const s = it.seller || {};

      return {
        "Order ID": order._id,
        "Status": order.status,
        "Order Date": dt(order.createdAt),

        "Customer Name": order.user?.name || "N/A",
        "Customer Email": order.user?.email || "N/A",
        "Customer Mobile": order.user?.mobile || "N/A",

        "City": order.address?.cityOrTown || "N/A",
        "State": order.address?.selectedState || "N/A",

        "Product Name": productName(p),
        "Product ID": p._id || "N/A",

        "Seller Name": sellerName(s),
        "Seller Email": s.email || "N/A",

        "Quantity": it.quantity || 0,
        "Price": it.price || 0,
        "IGST %": it.igst || 0,
        "Subtotal (w/o GST)": it.subtotalwithoutgst || 0,
        "Subtotal (with GST)": it.subtotalwithgst || 0,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Order Details");

    XLSX.writeFile(workbook, `Order_${order._id}.xlsx`);
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (err) {
    return <div className="p-6 text-red-600">{err}</div>;
  }

  if (!order) return null;

  const user = order.user || {};
  const address = order.address || {};
  const items = order.items || [];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Order Details</h1>
            <p className="text-sm">Order ID: {order._id}</p>
          </div>

          <div className="flex gap-2">
            {/* ✅ DOWNLOAD BUTTON */}
            <button
              onClick={downloadExcel}
              className="px-4 py-2 bg-green-600 text-white rounded"
            >
              ⬇ Download Excel
            </button>

            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 rounded"
            >
              Back
            </button>
          </div>
        </div>

        {/* ITEMS TABLE */}
        <div className="bg-white border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-left">Product</th>
                <th className="p-3 text-left">Seller</th>
                <th className="p-3 text-right">Qty</th>
                <th className="p-3 text-right">Price</th>
              </tr>
            </thead>

            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t">
                  <td className="p-3">{productName(it.product)}</td>
                  <td className="p-3">{sellerName(it.seller)}</td>
                  <td className="p-3 text-right">{it.quantity}</td>
                  <td className="p-3 text-right">{money(it.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;